/**
 * 공지사항 페이지
 * CRUD 기능 포함
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Announcement as AnnouncementIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  PushPin as PinIcon,
} from '@mui/icons-material';
import { useAuth } from '@/contexts/AuthContext';
import { getLevelNumber } from '@/config/menuConfig';
import { noticesAPI } from '@/services/api';

export default function NoticesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  // 다이얼로그 상태
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' | 'edit' | 'view'
  const [selectedNotice, setSelectedNotice] = useState(null);
  
  // 폼 상태
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    isImportant: false,
    isPinned: false,
  });

  // SaaS: 플랫폼 관리자만 공지 작성
  const canWrite = !!(user?.isAdmin || user?.role === 'admin');
  const currentLevel = getLevelNumber(user);

  // 공지사항 목록 로드
  useEffect(() => {
    loadNotices();
  }, []);

  const loadNotices = async () => {
    setLoading(true);
    try {
      const response = await noticesAPI.getAll({ limit: 50 });
      setNotices(response.data.data.notices || []);
    } catch (error) {
      console.error('Failed to load notices:', error);
      setSnackbar({
        open: true,
        message: '공지사항을 불러오는데 실패했습니다.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // 다이얼로그 열기
  const handleOpenDialog = (mode, notice = null) => {
    setDialogMode(mode);
    setSelectedNotice(notice);
    
    if (mode === 'create') {
      setFormData({
        title: '',
        content: '',
        isImportant: false,
        isPinned: false,
      });
    } else if (notice) {
      setFormData({
        title: notice.title,
        content: notice.content,
        isImportant: notice.is_important,
        isPinned: notice.is_pinned,
      });
    }
    
    setDialogOpen(true);
  };

  // 다이얼로그 닫기
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedNotice(null);
    setFormData({
      title: '',
      content: '',
      isImportant: false,
      isPinned: false,
    });
  };

  // 공지사항 저장
  const handleSave = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      setSnackbar({
        open: true,
        message: '제목과 내용을 입력해 주세요.',
        severity: 'warning'
      });
      return;
    }

    try {
      if (dialogMode === 'create') {
        await noticesAPI.create(formData);
        setSnackbar({
          open: true,
          message: '공지사항이 등록되었습니다.',
          severity: 'success'
        });
      } else if (dialogMode === 'edit') {
        await noticesAPI.update(selectedNotice.id, formData);
        setSnackbar({
          open: true,
          message: '공지사항이 수정되었습니다.',
          severity: 'success'
        });
      }
      
      handleCloseDialog();
      loadNotices();
    } catch (error) {
      console.error('Save notice error:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || '저장에 실패했습니다.',
        severity: 'error'
      });
    }
  };

  // 공지사항 삭제
  const handleDelete = async (notice) => {
    if (!window.confirm(`"${notice.title}" 공지사항을 삭제하시겠습니까?`)) {
      return;
    }

    try {
      await noticesAPI.delete(notice.id);
      setSnackbar({
        open: true,
        message: '공지사항이 삭제되었습니다.',
        severity: 'success'
      });
      loadNotices();
    } catch (error) {
      console.error('Delete notice error:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || '삭제에 실패했습니다.',
        severity: 'error'
      });
    }
  };

  // 수정/삭제 권한 확인
  const canEditNotice = (notice) => {
    if (!notice) return false;
    if (currentLevel >= 4) return true; // Level4 이상은 모든 공지 수정 가능
    return notice.author_id === user?.id; // 본인 글만 수정 가능
  };

  // 날짜 포맷
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          {t('nav.notices')}
        </Typography>
        {canWrite && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('create')}
          >
            공지사항 작성
          </Button>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ p: 0 }}>
          <List>
            {notices.length === 0 ? (
              <ListItem>
                <ListItemText 
                  primary="등록된 공지사항이 없습니다."
                  sx={{ textAlign: 'center', color: 'text.secondary' }}
                />
              </ListItem>
            ) : (
              notices.map((notice, index) => (
                <Box key={notice.id}>
                  <ListItem
                    sx={{ 
                      py: 2,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                    secondaryAction={
                      canEditNotice(notice) && (
                        <Box>
                          <IconButton 
                            size="small" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenDialog('edit', notice);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(notice);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )
                    }
                    onClick={() => handleOpenDialog('view', notice)}
                  >
                    <ListItemIcon>
                      <AnnouncementIcon color={notice.is_important ? 'error' : 'action'} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {notice.is_pinned && (
                            <PinIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                          )}
                          {notice.is_important && (
                            <Chip label="중요" color="error" size="small" />
                          )}
                          <Typography variant="subtitle1" fontWeight="medium">
                            {notice.title}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">
                            {notice.author_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(notice.created_at)}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <VisibilityIcon sx={{ fontSize: 14 }} />
                            <Typography variant="body2" color="text.secondary">
                              {notice.views || 0}
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < notices.length - 1 && <Divider />}
                </Box>
              ))
            )}
          </List>
        </Paper>
      )}

      {/* 공지사항 다이얼로그 */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {dialogMode === 'create' && '공지사항 작성'}
          {dialogMode === 'edit' && '공지사항 수정'}
          {dialogMode === 'view' && '공지사항'}
          <IconButton onClick={handleCloseDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {dialogMode === 'view' ? (
            // 상세 보기
            <Box>
              <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
                {selectedNotice?.is_important && (
                  <Chip label="중요" color="error" size="small" />
                )}
                {selectedNotice?.is_pinned && (
                  <Chip label="상단 고정" color="primary" size="small" variant="outlined" />
                )}
              </Box>
              <Typography variant="h5" gutterBottom fontWeight="bold">
                {selectedNotice?.title}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  작성자: {selectedNotice?.author_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  작성일: {formatDate(selectedNotice?.created_at)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  조회수: {selectedNotice?.views || 0}
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Typography 
                variant="body1" 
                sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}
              >
                {selectedNotice?.content}
              </Typography>
            </Box>
          ) : (
            // 작성/수정 폼
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="제목"
                fullWidth
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
              <TextField
                label="내용"
                fullWidth
                multiline
                rows={10}
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                required
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isImportant}
                      onChange={(e) => setFormData({ ...formData, isImportant: e.target.checked })}
                    />
                  }
                  label="중요 공지"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isPinned}
                      onChange={(e) => setFormData({ ...formData, isPinned: e.target.checked })}
                    />
                  }
                  label="상단 고정"
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            {dialogMode === 'view' ? '닫기' : '취소'}
          </Button>
          {dialogMode !== 'view' && (
            <Button variant="contained" onClick={handleSave}>
              {dialogMode === 'create' ? '등록' : '저장'}
            </Button>
          )}
          {dialogMode === 'view' && canEditNotice(selectedNotice) && (
            <Button 
              variant="outlined" 
              startIcon={<EditIcon />}
              onClick={() => {
                setDialogMode('edit');
                setFormData({
                  title: selectedNotice.title,
                  content: selectedNotice.content,
                  isImportant: selectedNotice.is_important,
                  isPinned: selectedNotice.is_pinned,
                });
              }}
            >
              수정
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
